import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import {
  CartExceptionEnum,
  IAddToCartErrorResponse,
  IAddToCartGroupedError,
  IAddToCartResult,
  INewCartItem,
} from "@gloria-public/order/domain/interfaces/cart.interface";
import { OrderService } from "@gloria-public/order/domain/services/order.service";
import {
  IAnswer,
  IDnaNextSteps,
  IRuleResultSerializable,
  IScoringInterpretationSerializable,
  MessageType,
  RuleResultType,
} from "@gloria-public/questionnaire/domain/interfaces/questionnaire-response.interface";
import {
  EnableWhenBehavior,
  IQuestion,
  IQuestionnaireSerializable,
} from "@gloria-public/questionnaire/domain/interfaces/questionnaire.interface";
import { ISlideView } from "@gloria-public/questionnaire/domain/interfaces/slide-view.interface";
import { IRiskDetailsResponse } from "@gloria-public/risk/domain/interfaces/knowledge.interface";
import { RiskService } from "@gloria-public/risk/domain/services/risk.service";
import { TranslateService } from "@ngx-translate/core";
import { AddToCartErrorComponent } from "@shared/components/add-to-cart-error/add-to-cart-error.component";
import { ICoding } from "@shared/interfaces/shared.interface";
import { SessionService } from "@shared/services/session.service";
import { Tools } from "@shared/utils/helpers/tools";
import { ITranslation } from "@shared/utils/helpers/translation.interface";
import { Subject, catchError, concatMap, distinctUntilChanged, first, from, map, of, startWith, takeUntil, toArray } from "rxjs";

@Component({
  selector: "app-questionnaire-completed-page",
  templateUrl: "./questionnaire-completed-page.component.html",
  styleUrls: ["./questionnaire-completed-page.component.scss"],
})
export class QuestionnaireCompletedPageComponent implements OnInit, OnDestroy {
  public allQuestionnaires: string[] = [];
  public currentIndex = 0;
  public hasNext = false;
  public nextQuestionnaireId: string | null = null;
  public riskId = "";
  public isAddingToCart = false;
  public isLoadingRiskDetails = true;
  public riskDetails!: IRiskDetailsResponse;
  public score?: number;
  public interpretation?: IScoringInterpretationSerializable;
  public nextSteps: IRuleResultSerializable[] = [];
  public dna?: IDnaNextSteps;
  public slideViews: ISlideView[] = [];
  public questionnaire!: IQuestionnaireSerializable;
  public showOrderButtons = false;
  public serviceRequestToAdd!: string;
  public MessageType = MessageType;
  public RuleResultType = RuleResultType;
  public interpretationRange = "";
  public dnaSelected = false;
  public selectedServiceRequests: Set<string> = new Set();
  public availableServiceRequests: Array<{ value: string; message: ITranslation }> = [];

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private orderService: OrderService,
    private translateService: TranslateService,
    public snackBar: MatSnackBar,
    private riskService: RiskService,
    private dialog: MatDialog,
    public sessionService: SessionService
  ) {
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras?.state;

    if (state) {
      this.allQuestionnaires = state["allQuestionnaires"] ?? [];
      this.currentIndex = state["currentIndex"] ?? 0;
      this.hasNext = state["hasNext"] ?? false;
      this.nextQuestionnaireId = state["nextQuestionnaireId"] ?? null;
      this.riskId = state["riskId"] ?? "";
      this.score = state["score"] ?? null;
      this.interpretation = state["interpretation"] ?? null;
      this.nextSteps = state["nextSteps"] ?? [];
      this.slideViews = state["slideViews"] ?? [];
      this.questionnaire = state["questionnaire"] ?? null;
      this.dna = state["dna"] ?? null;
      if (this.dna) {
        this.dnaSelected = !this.dna.hasDNA;
      }
    } else {
      // If direct access via url, so without state, redirect back.
      // QuestionnairePageComponent will load the state and redirect tot his page
      this.router.navigate([".."], { relativeTo: this.route });
    }

    this.processNextSteps();
    this.interpretationRange = this.calculateInterpretationRange();
  }

  /**
   * Initializes the component, validates navigation state, and loads risk details.
   */
  ngOnInit(): void {
    // If no state, redirect back
    if (!this.riskId) {
      this.router.navigate(["/risks"]);
      return;
    }

    this.translateService.onLangChange
      .pipe(
        startWith({
          lang: this.translateService.currentLang,
        }),
        distinctUntilChanged((a, b) => a.lang === b.lang),
        takeUntil(this.onDestroy$)
      )
      .subscribe((event) => {
        this.loadRiskDetails(event.lang);
      });
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Calculates the interpretation range from interpretation conditions.
   * @return Formatted interpretation range string.
   */
  private calculateInterpretationRange(): string {
    if (!this.interpretation?.enableWhen || this.interpretation.enableWhen.length === 0) {
      return "";
    }

    const values = this.interpretation.enableWhen.map((condition) => parseFloat(condition.answer)).filter((val) => !isNaN(val));

    if (values.length === 0) return "";

    const min = Math.min(...values);
    const max = Math.max(...values);

    if (min === max) {
      return `${min}`;
    }

    return `${min} - ${max}`;
  }

  /**
   * Processes questionnaire next steps and extracts service requests and questionnaires.
   */
  private processNextSteps(): void {
    if (!this.nextSteps || this.nextSteps.length === 0) {
      return;
    }

    for (const step of this.nextSteps) {
      if (step.resultType === RuleResultType.SERVICE_REQUEST) {
        if (step.value) {
          this.availableServiceRequests.push({
            value: step.value,
            message: step.message,
          });
          this.selectedServiceRequests.add(step.value);
        }
      } else if (step.resultType === RuleResultType.QUESTIONNAIRE) {
        if (step.value && !this.allQuestionnaires.includes(step.value)) {
          this.allQuestionnaires.push(step.value);
          this.hasNext = true;
          if (!this.nextQuestionnaireId) {
            this.nextQuestionnaireId = step.value;
          }
        }
      }
    }

    this.showOrderButtons = this.availableServiceRequests.length > 0;
  }

  /**
   * Navigates to the next questionnaire in the sequence if available.
   */
  public startNextQuestionnaire(): void {
    if (this.nextQuestionnaireId) {
      this.router.navigate(["/risks", this.riskId, "questionnaires", this.nextQuestionnaireId], {
        state: {
          allQuestionnaires: this.allQuestionnaires,
        },
      });
    }
  }

  /**
   * Toggles the selected state of a service request.
   * @param value Identifier of the service request to toggle.
   */
  public toggleServiceRequest(value: string): void {
    if (this.selectedServiceRequests.has(value)) {
      this.selectedServiceRequests.delete(value);
    } else {
      this.selectedServiceRequests.add(value);
    }
  }

  /**
   * Determines whether a service request is currently selected.
   * @param value Identifier of the service request to check.
   * @return True if the service request is selected; otherwise false.
   */
  public isServiceRequestSelected(value: string): boolean {
    return this.selectedServiceRequests.has(value);
  }

  /**
   * Loads detailed information for the current risk.
   * @param lang Current language
   */
  private loadRiskDetails(lang: string): void {
    this.riskService
      .getRisk(this.riskId, lang)
      .pipe(first())
      .subscribe({
        next: (risk) => {
          if (risk) {
            this.riskDetails = risk;
          }
          this.isLoadingRiskDetails = false;
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
            duration: 5000,
          });
          this.isLoadingRiskDetails = false;
        },
      });
  }

  /**
   * Adds selected service requests to the shopping cart and optionally navigates to the cart page.
   * @param navigate Indicates whether to navigate to the cart after successful addition.
   */
  public addServiceRequestToCart(navigate = false): void {
    if (this.isAddingToCart || (this.selectedServiceRequests.size === 0 && !this.dnaSelected)) {
      return;
    }

    this.isAddingToCart = true;

    const cartItems: INewCartItem[] = [];

    if (this.dnaSelected && this.dna) {
      cartItems.push({
        serviceRequestID: this.dna.serviceRequest,
        riskTemplateID: this.dna.risk,
      });
    }

    cartItems.push(
      ...Array.from(this.selectedServiceRequests).map((serviceRequestId) => ({
        serviceRequestID: serviceRequestId,
        riskTemplateID: this.riskId,
      }))
    );

    from(cartItems)
      .pipe(
        concatMap((newCartItem) => {
          let name: string;

          const dna = this.dna;
          if (dna && newCartItem.serviceRequestID === dna.serviceRequest) {
            name = dna.message
              ? Tools.getTranslation(dna.message, this.translateService.currentLang as keyof ITranslation, "")
              : dna.serviceRequest;
          } else {
            const item = this.availableServiceRequests.find((i) => i.value === newCartItem.serviceRequestID);

            name = item?.message
              ? Tools.getTranslation(item.message, this.translateService.currentLang as keyof ITranslation, "")
              : item?.value || newCartItem.serviceRequestID;
          }

          return this.orderService.addItemToCart(newCartItem, this.translateService.currentLang).pipe(
            map(() => ({
              status: "success" as const,
              name,
              item: newCartItem,
            })),
            catchError((err) =>
              of({
                status: err?.status === 409 ? ("conflict" as const) : ("error" as const),
                name,
                item: newCartItem,
                error: err?.error as IAddToCartErrorResponse,
              })
            )
          );
        }),
        toArray()
      )
      .subscribe((results: IAddToCartResult[]) => {
        this.isAddingToCart = false;

        const conflicts = results.filter((r) => r.status === "conflict");
        const errors = results.filter((r) => r.status === "error");
        const successes = results.filter((r) => r.status === "success");

        const conflictsGrouped = Object.values(
          conflicts.reduce(
            (acc, conflict) => {
              if (!conflict.error) return acc;

              const code = conflict.error.error;

              if (!acc[code]) {
                acc[code] = {
                  code,
                  items: [],
                };
              }

              acc[code].items.push({
                item: conflict.item,
                name: conflict.name,
              });

              return acc;
            },
            {} as Record<string, IAddToCartGroupedError>
          )
        );

        if (errors.length > 0) {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
        }

        if (conflicts.length > 0) {
          const required = conflicts.find((c) => c.error?.error === CartExceptionEnum.DNA_NOT_DECODED)?.error?.required;

          this.dialog.open(AddToCartErrorComponent, {
            maxWidth: "100vw",
            maxHeight: "100vh",
            data: { errors: conflictsGrouped, required },
          });
        }

        if (successes.length > 0) {
          this.snackBar.open(
            this.translateService.instant(
              successes.length > 1 ? "page.questionnaire.completed.success_plural" : "page.questionnaire.completed.success",
              { count: successes.length }
            ),
            "OK",
            { duration: 3000 }
          );
        }

        if (navigate && errors.length === 0) {
          this.router.navigate(["/cart"]);
        }
      });
  }

  /**
   * Indicates whether there are any next steps that are not service requests.
   * @returns `true` if at least one next step is not a service request; otherwise, `false`.
   */
  public get hasNonServiceRequestSteps(): boolean {
    return this.nextSteps.some((step) => step.resultType !== RuleResultType.SERVICE_REQUEST);
  }

  /**
   * Retrieves the available coding choices for a questionnaire question.
   * @param question Question whose choices should be retrieved.
   * @return Array of available coding options.
   */
  public getChoices(question: IQuestion): ICoding[] {
    if (!question.options?.reference) return [];

    const valueSet = this.questionnaire.contained.find((c) => c.idSet === question.options.reference);

    return valueSet?.compose.include[0].concept || [];
  }

  /**
   * Evaluates whether a question should be enabled based on enableWhen conditions.
   * @param question Question whose visibility should be evaluated.
   * @return True if the question should be enabled; otherwise false.
   */
  public isQuestionEnabled(question: IQuestion): boolean {
    if (!question.enableWhen || question.enableWhen.length === 0) {
      return true;
    }

    const behavior = question.enableWhenBehavior || EnableWhenBehavior.AND;

    for (const enableWhen of question.enableWhen) {
      const dependentAnswer = this.findAnswer(enableWhen.question ?? "");
      if (!dependentAnswer || dependentAnswer.length === 0) {
        if (behavior === EnableWhenBehavior.AND) return false;
        continue;
      }

      const value = enableWhen.type === "number" ? Number(dependentAnswer[0].valueCoding.code) : dependentAnswer[0].valueCoding.code;

      const expectedValue = enableWhen.type === "number" ? Number(enableWhen.answer) : enableWhen.answer;

      let isEnabled = false;
      switch (enableWhen.operator) {
        case "=":
          isEnabled = value === expectedValue;
          break;
        case "!=":
          isEnabled = value !== expectedValue;
          break;
        case ">":
          isEnabled = value > expectedValue;
          break;
        case "<":
          isEnabled = value < expectedValue;
          break;
        case ">=":
          isEnabled = value >= expectedValue;
          break;
        case "<=":
          isEnabled = value <= expectedValue;
          break;
      }

      if (behavior === EnableWhenBehavior.OR && isEnabled) return true;
      if (behavior === EnableWhenBehavior.AND && !isEnabled) return false;
    }

    return behavior === EnableWhenBehavior.AND;
  }

  /**
   * Finds the answer for a question by its link identifier.
   * @param linkId Link identifier of the question to search for.
   * @return Array of answers if found; otherwise null.
   */
  private findAnswer(linkId: string): IAnswer[] | null {
    for (const slide of this.slideViews) {
      if (slide.questions) {
        const questionView = slide.questions.find((q) => q.question.linkId === linkId);
        if (questionView && questionView?.response?.answer) {
          return questionView.response.answer;
        }
      }
    }
    return null;
  }
}
